import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import {
  useActivityPopupPriority,
  useIsHomeDisplayReady,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
import { useHomeDisplayStore } from "@/modules/homeDisplay/stores";
import { useUserDataFlattened } from "@/modules/user/stores";
import { ROUTES } from "@/types/routes";
import {
  FIRST_RECHARGE_STAGED_PENDING_CLAIM_EVENT,
  SESSION_PENDING_STAGED_FIRST_DEPOSIT_CLAIM,
} from "@/constants/firstRechargeStagedSession";
import { firstRechargeStagedApi } from "@/api/firstRechargeStagedApi";

const FirstRechargeStagedClaimDialog = lazy(() =>
  import(
    "../components/FirstRechargeStaged/FirstRechargeStagedClaimDialog"
  ).then(m => ({ default: m.FirstRechargeStagedClaimDialog }))
);

const STAGED_CFG = POPUP_CONFIGS.FIRST_RECHARGE_STAGED_CLAIM;

export const useFirstRechargeStagedClaimPopup = () => {
  const { pathname } = useLocation();
  const { isAuthenticated, data: userInfo } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  const snapshot = useHomeDisplayStore(
    s => s.data?.firstRechargeStaged ?? null
  );

  /** 与首充浮标共用运营配置：popupTimings 里用 FIRST_DEPOSIT 即可 */
  const priority = useActivityPopupPriority(
    ActivityType.FIRST_DEPOSIT,
    STAGED_CFG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );

  const shouldShowStaged = useCallback(() => {
    if (!snapshot) return false;
    if (snapshot.firstDepositMode !== "STAGED_MANUAL") return false;
    if (!snapshot.showStagedUi) return false;
    return true;
  }, [snapshot]);

  const shouldShowFn = useCallback(
    (ctx?: { isManualTrigger?: boolean }) => {
      if (!isConfigReady || !isAuthenticated) return false;
      if (!shouldShowStaged()) return false;
      if (ctx?.isManualTrigger) return true;
      return false;
    },
    [isConfigReady, isAuthenticated, shouldShowStaged]
  );

  const { trigger } = useQueuedPopup({
    popupId: STAGED_CFG.id,
    priority,
    shouldShow: shouldShowFn,
    renderContent: onComplete => (
      <Suspense fallback={null}>
        <FirstRechargeStagedClaimDialog visible onClose={onComplete} />
      </Suspense>
    ),
    showOnce: false,
    allowManualTrigger: true,
    autoTrigger: false,
    dependencies: [
      isConfigReady,
      isAuthenticated,
      userInfo?.userId,
      snapshot?.rechargeRecordId,
      snapshot?.showStagedUi,
      snapshot?.firstDepositMode,
    ],
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/"],
    blockedPaths: [
      ROUTES.LOGIN,
      ROUTES.REGISTER,
      ROUTES.RECHARGE,
      ROUTES.WITHDRAW,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId as string | undefined,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  /** session 写入与 RECHARGE_SUCCESS 同 tick 时，pathname 未变也需再跑领取逻辑 */
  const [pendingClaimSignal, setPendingClaimSignal] = useState(0);
  useEffect(() => {
    const bump = () => setPendingClaimSignal(n => n + 1);
    window.addEventListener(FIRST_RECHARGE_STAGED_PENDING_CLAIM_EVENT, bump);
    return () => {
      window.removeEventListener(
        FIRST_RECHARGE_STAGED_PENDING_CLAIM_EVENT,
        bump
      );
    };
  }, []);

  const remainingMs = useMemo(() => {
    const end = snapshot?.floatingCountdownEndAtMs ?? 0;
    return Math.max(0, end - Date.now());
  }, [snapshot?.floatingCountdownEndAtMs, snapshot?.secondsToExpire]);

  /**
   * 仅当充值成功流程写入 session 后回首页时自动打开领取窗；
   * 纯登录/无此标记不会触发。
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isConfigReady || !isAuthenticated) return;
    const path = pathname;
    const isHome =
      path === ROUTES.HOME || path === ROUTES.HOME_ALT || path === "/";
    if (!isHome) return;
    if (
      sessionStorage.getItem(SESSION_PENDING_STAGED_FIRST_DEPOSIT_CLAIM) !== "1"
    ) {
      return;
    }

    let cancelled = false;
    let triggerTimeoutId: number | undefined;

    const canOpen = () => {
      const snap = useHomeDisplayStore.getState().data?.firstRechargeStaged;
      return (
        !!snap &&
        snap.firstDepositMode === "STAGED_MANUAL" &&
        !!snap.showStagedUi
      );
    };

    const run = async () => {
      const patch = useHomeDisplayStore.getState().patchFirstRechargeStaged;
      const refresh = useHomeDisplayStore.getState().refreshConfig;

      for (let attempt = 0; attempt < 5; attempt++) {
        if (cancelled) return;
        if (canOpen()) break;
        if (attempt > 0) {
          await new Promise<void>(resolve => {
            window.setTimeout(() => resolve(), 400);
          });
        }
        try {
          const remote = await firstRechargeStagedApi.fetchSnapshot();
          patch(remote);
        } catch {
          /* ignore */
        }
        try {
          await refresh();
        } catch {
          /* ignore */
        }
      }

      if (cancelled) return;
      if (!canOpen()) {
        sessionStorage.removeItem(SESSION_PENDING_STAGED_FIRST_DEPOSIT_CLAIM);
        return;
      }
      sessionStorage.removeItem(SESSION_PENDING_STAGED_FIRST_DEPOSIT_CLAIM);
      triggerTimeoutId = window.setTimeout(() => {
        if (!cancelled) triggerRef.current();
      }, 500);
    };

    void run();

    return () => {
      cancelled = true;
      if (triggerTimeoutId !== undefined) {
        window.clearTimeout(triggerTimeoutId);
      }
    };
  }, [
    pathname,
    isConfigReady,
    isAuthenticated,
    snapshot?.rechargeRecordId,
    snapshot?.firstDepositMode,
    snapshot?.showStagedUi,
    pendingClaimSignal,
  ]);

  return {
    trigger,
    shouldShowFloating: shouldShowStaged(),
    remainingMs,
    claimableCount: snapshot?.claimableCount ?? 0,
  };
};
